import { Suspense } from "react";
import { Route, Routes } from "react-router";
import { Button } from "@/components/ui/button";
import { useT } from "../shell/i18n";
import { Boundary } from "../ui/boundary";
import { useModuleNav } from "../ui/module-nav";
import { RouteLoading } from "../ui/route-loading";
import { RouteStage } from "../ui/route-stage";
import type { AppScreen, ScreenContext } from "./application-route-types";
import { FOUNDATION_SCREENS } from "./application-routes-foundation";
import { OPERATIONS_SCREENS } from "./application-routes-operations";
import { TALENT_SCREENS } from "./application-routes-talent";
import { WORK_SCREENS } from "./application-routes-work";
import { lazyScreen } from "./lazy-screen";
import { NotFound } from "./not-found";
import { usePageQuery } from "./page-query";
import { RECORDS } from "./records";

const RecordScreen = lazyScreen(() => import("./record-screen"), "RecordScreen");

const RECORD_SCREENS: readonly AppScreen[] = RECORDS.map((record) => ({
  path: record.at,
  render: ({ state }) => (
    <RecordScreen
      record={record}
      roles={state.riksu.roles ?? []}
      mine={state.riksu.person?.person_id ?? null}
    />
  ),
}));

const SCREENS: readonly AppScreen[] = [
  ...FOUNDATION_SCREENS,
  ...WORK_SCREENS,
  ...TALENT_SCREENS,
  ...OPERATIONS_SCREENS,
  ...RECORD_SCREENS,
];

export const ROUTE_PATHS: readonly string[] = SCREENS.map((screen) => screen.path);

export function ApplicationRoutes({ state, administering, photograph }: ScreenContext) {
  const { at } = useModuleNav();
  const query = usePageQuery();
  return (
    <Boundary fallback={(error, retry) => <Broken error={error} onRetry={retry} />}>
      <RouteStage>
        <Suspense fallback={<RouteLoading />}>
          <Routes location={{ pathname: at, ...query }}>
            {SCREENS.map(({ path, render }) => (
              <Route
                key={path}
                path={path}
                element={render({ state, administering, photograph })}
              />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </RouteStage>
    </Boundary>
  );
}

function Broken({ error, onRetry }: { readonly error: Error; readonly onRetry: () => void }) {
  const t = useT();
  return (
    <div className="mx-auto flex max-w-(--frame-prose) flex-col items-start gap-3 px-4 py-12 lg:px-10">
      <h2 className="m-0 text-[length:var(--type-shell-surface-title-size)] font-medium">
        {t("broken.heading")}
      </h2>
      <p className="m-0 text-shell-body text-muted-foreground">{t("broken.detail")}</p>
      <details className="w-full">
        <summary className="cursor-default text-shell-body text-muted-foreground">
          {t("broken.technical")}
        </summary>
        <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-muted p-3 text-shell-caption">
          {error.message}
        </pre>
      </details>
      <Button onClick={onRetry}>{t("broken.retry")}</Button>
    </div>
  );
}
